import { useEffect, useState } from 'react'
import { RefreshCw, MapPin, DollarSign, ShoppingCart, Users, TrendingUp, Clock } from 'lucide-react'

interface StoreData {
  sales: number
  revenue: number
  customers: number
  averageTicket: number
  growth: string
  recentSales: Array<{
    id: string
    customer: string
    amount: number
    date: string
    products: number
  }>
  topProducts: Array<{
    id: string
    name: string
    sales: number
    revenue: number
  }>
}

export default function StoreColomboPage() {
  const [data, setData] = useState<StoreData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStoreData()
  }, [])

  const fetchStoreData = async () => {
    setLoading(true)
    // TODO: Implementar chamada real à API da loja
    setTimeout(() => {
      setData({
        sales: 0,
        revenue: 0,
        customers: 0,
        averageTicket: 0,
        growth: '+0%',
        recentSales: [],
        topProducts: []
      })
      setLoading(false)
    }, 1000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Loja CC Colombo</h1>
          <p className="text-gray-600 mt-1 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Centro Colombo, Lisboa, Portugal
          </p>
        </div>
        <button
          onClick={fetchStoreData}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vendas</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{data?.sales || 0}</p>
              <p className="text-sm text-black mt-1">{data?.growth || '+0%'}</p>
            </div>
            <ShoppingCart className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Receita</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">€{data?.revenue.toFixed(2) || '0.00'}</p>
            </div>
            <DollarSign className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Clientes</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{data?.customers || 0}</p>
            </div>
            <Users className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ticket Médio</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">€{data?.averageTicket.toFixed(2) || '0.00'}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-black" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Vendas Recentes</h2>
          </div>
          <div className="p-6">
            {data?.recentSales.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhuma venda encontrada</p>
            ) : (
              <div className="space-y-4">
                {data?.recentSales.map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{sale.customer}</p>
                      <p className="text-sm text-gray-500">{sale.products} produtos</p>
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {sale.date}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">€{sale.amount.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Produtos Mais Vendidos</h2>
          </div>
          <div className="p-6">
            {data?.topProducts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhum dado disponível</p>
            ) : (
              <div className="space-y-4">
                {data?.topProducts.map((product, index) => (
                  <div key={product.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.sales} vendas</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">€{product.revenue.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
